/**
 * Legal state transitions, declared as data. ARCHITECTURE.md §7.1.
 *
 * `tools/combat-test.mjs` walks this table to prove the state machine can never reach an
 * impossible configuration, and the state machine itself validates every transition in dev
 * builds. Declaring transitions as data (rather than burying them in `if` chains) is what
 * makes "no impossible overlapping states" an assertion instead of a hope.
 */

import { S, ALL_STATES } from './states.js';

/**
 * Transitions that any state may take, because they are driven by being hit, thrown,
 * or by the round ending — none of which the fighter can refuse.
 */
export const UNIVERSAL_TARGETS = Object.freeze([
  S.HITSTUN_STAND, S.HITSTUN_CROUCH, S.HITSTUN_LAUNCH, S.HITSTUN_AIR, S.HITSTUN_WALL,
  S.WALL_SPLAT, S.KNOCKDOWN_FALL, S.THROWN, S.FINISHER_VICTIM,
  S.ROUND_INTRO, S.ROUND_WIN, S.ROUND_LOSS, S.MATCH_WIN, S.MATCH_LOSS,
]);

/**
 * Explicit adjacency: `TRANSITIONS[from]` lists every state reachable by the fighter's own
 * volition or by the natural expiry of `from`.
 */
export const TRANSITIONS = Object.freeze({
  [S.IDLE]: [
    S.WALK_FWD, S.WALK_BACK, S.DASH_FWD, S.DASH_BACK, S.SIDESTEP_LEFT, S.SIDESTEP_RIGHT,
    S.CROUCH_ENTER, S.GUARD_STAND, S.ATTACK_STARTUP, S.THROW_ATTEMPT, S.PARRY_ACTIVE,
    S.STANCE_SHIFT, S.SPECIAL_CHARGE, S.AIRBORNE, S.FINISHER_ATTACKER,
  ],
  [S.WALK_FWD]: [S.IDLE, S.WALK_BACK, S.DASH_FWD, S.CROUCH_ENTER, S.GUARD_STAND,
    S.ATTACK_STARTUP, S.THROW_ATTEMPT, S.SIDESTEP_LEFT, S.SIDESTEP_RIGHT, S.PARRY_ACTIVE,
    S.STANCE_SHIFT, S.AIRBORNE],
  [S.WALK_BACK]: [S.IDLE, S.WALK_FWD, S.DASH_BACK, S.CROUCH_ENTER, S.GUARD_STAND,
    S.ATTACK_STARTUP, S.SIDESTEP_LEFT, S.SIDESTEP_RIGHT, S.PARRY_ACTIVE, S.STANCE_SHIFT,
    S.AIRBORNE],

  [S.DASH_FWD]: [S.IDLE, S.WALK_FWD, S.ATTACK_STARTUP, S.THROW_ATTEMPT, S.GUARD_STAND,
    S.CROUCH_ENTER, S.DASH_FWD],
  [S.DASH_BACK]: [S.BACKDASH_RECOVERY],
  [S.BACKDASH_RECOVERY]: [S.IDLE, S.WALK_BACK, S.WALK_FWD, S.GUARD_STAND, S.ATTACK_STARTUP,
    S.DASH_BACK, S.CROUCH_ENTER],

  [S.SIDESTEP_LEFT]: [S.SIDESTEP_RECOVERY, S.ATTACK_STARTUP, S.THROW_ATTEMPT],
  [S.SIDESTEP_RIGHT]: [S.SIDESTEP_RECOVERY, S.ATTACK_STARTUP, S.THROW_ATTEMPT],
  [S.SIDESTEP_RECOVERY]: [S.IDLE, S.WALK_FWD, S.WALK_BACK, S.GUARD_STAND, S.ATTACK_STARTUP,
    S.CROUCH_ENTER, S.THROW_ATTEMPT],

  [S.CROUCH_ENTER]: [S.CROUCH, S.CROUCH_EXIT, S.GUARD_CROUCH, S.ATTACK_STARTUP],
  [S.CROUCH]: [S.CROUCH_EXIT, S.GUARD_CROUCH, S.ATTACK_STARTUP, S.THROW_ATTEMPT, S.CROUCH_ENTER],
  [S.CROUCH_EXIT]: [S.IDLE, S.WALK_FWD, S.WALK_BACK, S.GUARD_STAND, S.ATTACK_STARTUP,
    S.CROUCH_ENTER, S.DASH_FWD, S.DASH_BACK],

  [S.GUARD_STAND]: [S.IDLE, S.GUARD_CROUCH, S.GUARD_RECOVERY, S.WALK_BACK, S.WALK_FWD,
    S.ATTACK_STARTUP, S.THROW_ATTEMPT, S.DASH_BACK, S.SIDESTEP_LEFT, S.SIDESTEP_RIGHT,
    S.PARRY_ACTIVE, S.CROUCH_ENTER],
  [S.GUARD_CROUCH]: [S.CROUCH, S.GUARD_STAND, S.GUARD_RECOVERY, S.ATTACK_STARTUP, S.CROUCH_EXIT],
  [S.GUARD_RECOVERY]: [S.IDLE, S.GUARD_STAND, S.GUARD_CROUCH, S.WALK_BACK, S.ATTACK_STARTUP,
    S.CROUCH_ENTER, S.DASH_BACK],

  [S.PARRY_ACTIVE]: [S.IDLE, S.GUARD_STAND, S.PARRY_SUCCESS, S.GUARD_RECOVERY],
  [S.PARRY_SUCCESS]: [S.IDLE, S.ATTACK_STARTUP, S.THROW_ATTEMPT, S.STANCE_SHIFT, S.GUARD_STAND],

  [S.ATTACK_STARTUP]: [S.ATTACK_ACTIVE, S.ATTACK_RECOVERY, S.AIR_ATTACK],
  [S.ATTACK_ACTIVE]: [S.ATTACK_RECOVERY, S.ATTACK_ACTIVE, S.ATTACK_STARTUP, S.FINISHER_ATTACKER],
  [S.ATTACK_RECOVERY]: [S.IDLE, S.ATTACK_STARTUP, S.CROUCH, S.GUARD_STAND, S.WALK_FWD,
    S.WALK_BACK, S.STANCE_SHIFT, S.THROW_ATTEMPT, S.CROUCH_ENTER, S.LANDING],

  [S.THROW_ATTEMPT]: [S.THROW_HOLD, S.ATTACK_RECOVERY, S.IDLE],
  [S.THROW_HOLD]: [S.THROW_EXECUTE, S.THROW_BROKEN],
  [S.THROW_EXECUTE]: [S.IDLE, S.ATTACK_RECOVERY, S.THROW_ATTEMPT, S.THROW_HOLD],
  [S.THROW_BROKEN]: [S.IDLE, S.GUARD_STAND],
  // THROW_TECH is reachable because a grabbed fighter who breaks the throw during the window
  // escapes from THROWN rather than from a neutral state.
  [S.THROWN]: [S.KNOCKDOWN_FALL, S.KNOCKDOWN_GROUND, S.HITSTUN_STAND, S.WALL_SPLAT, S.HITSTUN_AIR,
    S.THROW_TECH],
  [S.THROW_TECH]: [S.IDLE, S.GUARD_STAND],

  [S.HITSTUN_STAND]: [S.IDLE, S.GUARD_STAND, S.KNOCKDOWN_FALL, S.HITSTUN_CROUCH, S.WALL_SPLAT],
  [S.HITSTUN_CROUCH]: [S.CROUCH, S.IDLE, S.GUARD_CROUCH, S.KNOCKDOWN_FALL],
  [S.HITSTUN_LAUNCH]: [S.HITSTUN_AIR, S.KNOCKDOWN_FALL, S.WALL_SPLAT],
  [S.HITSTUN_AIR]: [S.KNOCKDOWN_FALL, S.HITSTUN_AIR, S.WALL_SPLAT, S.HITSTUN_LAUNCH],
  [S.HITSTUN_WALL]: [S.WALL_SPLAT, S.KNOCKDOWN_FALL, S.HITSTUN_STAND],

  [S.WALL_SPLAT]: [S.WALL_SLUMP, S.HITSTUN_STAND, S.KNOCKDOWN_FALL, S.HITSTUN_AIR],
  [S.WALL_SLUMP]: [S.KNOCKDOWN_GROUND, S.IDLE, S.HITSTUN_STAND],

  [S.KNOCKDOWN_FALL]: [S.KNOCKDOWN_GROUND, S.WALL_SPLAT],
  [S.KNOCKDOWN_GROUND]: [S.WAKEUP_NEUTRAL, S.WAKEUP_ATTACK, S.WAKEUP_ROLL, S.KNOCKDOWN_GROUND],
  [S.WAKEUP_NEUTRAL]: [S.IDLE, S.GUARD_STAND, S.CROUCH],
  [S.WAKEUP_ATTACK]: [S.ATTACK_ACTIVE, S.ATTACK_RECOVERY],
  [S.WAKEUP_ROLL]: [S.IDLE, S.GUARD_STAND, S.CROUCH, S.KNOCKDOWN_GROUND],

  [S.AIRBORNE]: [S.AIR_ATTACK, S.LANDING, S.HARD_LANDING],
  [S.AIR_ATTACK]: [S.AIRBORNE, S.LANDING, S.HARD_LANDING, S.AIR_ATTACK],
  [S.LANDING]: [S.IDLE, S.WALK_FWD, S.WALK_BACK, S.GUARD_STAND, S.ATTACK_STARTUP, S.CROUCH_ENTER],
  [S.HARD_LANDING]: [S.IDLE, S.GUARD_STAND, S.KNOCKDOWN_GROUND],

  [S.STANCE_SHIFT]: [S.IDLE, S.ATTACK_STARTUP, S.THROW_ATTEMPT, S.WALK_FWD, S.WALK_BACK,
    S.GUARD_STAND, S.DASH_FWD, S.DASH_BACK, S.SIDESTEP_LEFT, S.SIDESTEP_RIGHT, S.CROUCH_ENTER,
    S.PARRY_ACTIVE],
  [S.SPECIAL_CHARGE]: [S.ATTACK_STARTUP, S.ATTACK_ACTIVE, S.IDLE, S.ATTACK_RECOVERY],
  [S.FINISHER_ATTACKER]: [S.IDLE, S.ROUND_WIN, S.MATCH_WIN, S.ATTACK_RECOVERY],
  [S.FINISHER_VICTIM]: [S.KNOCKDOWN_GROUND, S.ROUND_LOSS, S.MATCH_LOSS, S.KNOCKDOWN_FALL],

  [S.ROUND_INTRO]: [S.IDLE],
  [S.ROUND_WIN]: [S.ROUND_INTRO, S.IDLE, S.MATCH_WIN],
  [S.ROUND_LOSS]: [S.ROUND_INTRO, S.IDLE, S.MATCH_LOSS],
  [S.MATCH_WIN]: [S.ROUND_INTRO, S.IDLE],
  [S.MATCH_LOSS]: [S.ROUND_INTRO, S.IDLE],
});

const UNIVERSAL_SET = new Set(UNIVERSAL_TARGETS);

/** Precomputed adjacency sets for O(1) validation on the hot path. */
const ADJACENCY = new Map();
for (const from of ALL_STATES) {
  const set = new Set(TRANSITIONS[from] || []);
  for (const t of UNIVERSAL_TARGETS) set.add(t);
  set.add(from); // self-transition is always legal (re-entry resets stateTick)
  ADJACENCY.set(from, set);
}

/**
 * @param {string} from
 * @param {string} to
 * @returns {boolean}
 */
export function isLegalTransition(from, to) {
  const set = ADJACENCY.get(from);
  if (!set) return false;
  return set.has(to);
}

export function legalTargets(from) {
  const set = ADJACENCY.get(from);
  return set ? [...set] : [];
}

export function isUniversalTarget(state) {
  return UNIVERSAL_SET.has(state);
}

/**
 * Validates the whole table. Called by tools/combat-test.mjs and tests/unit.
 * @returns {string[]} list of problems; empty means the table is sound.
 */
export function validateTransitionTable() {
  const problems = [];
  const known = new Set(ALL_STATES);

  for (const from of Object.keys(TRANSITIONS)) {
    if (!known.has(from)) problems.push(`TRANSITIONS has unknown source state "${from}"`);
    for (const to of TRANSITIONS[from]) {
      if (!known.has(to)) problems.push(`"${from}" -> unknown state "${to}"`);
    }
  }
  for (const s of ALL_STATES) {
    if (!TRANSITIONS[s]) problems.push(`state "${s}" has no TRANSITIONS entry`);
  }
  for (const t of UNIVERSAL_TARGETS) {
    if (!known.has(t)) problems.push(`UNIVERSAL_TARGETS has unknown state "${t}"`);
  }

  // Every state must be able to get back to IDLE, or the fighter can soft-lock.
  const reachesIdle = new Set([S.IDLE]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const from of ALL_STATES) {
      if (reachesIdle.has(from)) continue;
      const targets = ADJACENCY.get(from);
      for (const to of targets) {
        if (to !== from && reachesIdle.has(to)) {
          reachesIdle.add(from);
          grew = true;
          break;
        }
      }
    }
  }
  for (const s of ALL_STATES) {
    if (!reachesIdle.has(s)) problems.push(`state "${s}" cannot reach idle — soft-lock risk`);
  }

  return problems;
}
